<template>
    <div :class="{'tunnel-loading': isLoading}" @click="clicked">
        <slot :is-loading="isLoading" />
    </div>
</template>

<script>
/**
 * This component should contain one or more <a> elements.
 * When the user clicks on one of them, the user will first be sent to the given href prop, which will then redirect to the original link.
 * This functionality can be enabled or disabled and the redirect parameter can be changed as needed (e.g. return_url, passthrough, tunnel).
 * This component can be safely nested and the user will be redirected multiple times.
 */
export default {
    name: "ATunnel",
    props: {
        enabled: {type: Boolean, default: true},
        href: {type: String, required: true},
        redirectParam: {type: String, default: 'redirect'},
    },

    data() {
        return {
            timer: null,
            wasViable: false,
            previousLinks: [],
            isLoading: false,
        };
    },

    computed: {
        isViable() {
            return Boolean(this.enabled && this.href && this.href.length);
        }
    },

    mounted() {
        this.wasViable = this.isViable;
        if (this.isViable) {
            this.install();
        }
    },

    beforeUpdate() {
        if (this.isViable && this.wasViable) {
            this.uninstall();
        }
    },

    updated() {
        if (this.isViable && this.wasViable) {
            this.install();
        }
    },

    beforeDestroy() {
        if (this.isViable) {
            this.uninstall();
        }
    },

    watch: {
        isViable(newValue, oldValue) {
            this.wasViable = oldValue;

            if (newValue && !oldValue) {
                this.install();
            } else if (oldValue && !newValue) {
                this.uninstall();
            }

            this.$nextTick(() => {
                this.wasViable = this.isViable;
            });
        }
    },


    methods: {
        clicked() {
            if (this.isViable) {
                this.isLoading = true;
            }
        },

        install() {
            this.$nextTick(() =>
                this.getLinks().forEach(a => {
                    const href = a.getAttribute('href');
                    const url = new URL(this.href);
                    if (this.redirectParam) {
                        url.searchParams.set(this.redirectParam, href);
                    }
                    a.setAttribute('href', url.toString());
                    this.previousLinks.push({a, href});
                })
            );
        },

        uninstall() {
            this.getLinks().forEach(a => {
                const entry = this.previousLinks.find(entry => entry.a === a);
                if (entry) {
                    a.setAttribute('href', entry.href);
                }
            });
            this.previousLinks = [];
        },

        getLinks() {
            return Array.from(this.$el.getElementsByTagName('a'));
        },
    }
}
</script>
